import { useState } from 'react';
import { useTheme } from 'styled-components';

import LinkIcon from '~components/Icon/LinkIcon';
import ScrapIcon from '~components/Icon/ScrapIcon';
import ShareIcon from '~components/Icon/ShareIcon';
import Modal from '~components/Modal';
import useBookmark from '~hooks/query/bookmark/useBookmark';
import useDeleteBookmarks from '~hooks/query/bookmark/useDeleteBookmarks';
import usePostBookmarks from '~hooks/query/bookmark/usePostBookmarks';
import { CompanyInfoResponse } from '~hooks/query/company/useCompanyInfo';
import useGetMyInfoQuery from '~hooks/query/user/useMyInfoQuery';
import useGetIndustryByCode from '~hooks/useGetIndustryByCode';
import useMoveOneIdPage from '~hooks/useMoveOneIdPage';
import { useToastStore } from '~store/toast';
import copyToClipboard from '~utils/common/copyToClipboard';
import { setGtmDataLayer } from '~utils/common/thirdPartyEvent';

import { S } from './style';

interface Props {
  companyInfo?: CompanyInfoResponse;
}
function ActionButton({ companyInfo }: Props) {
  const { isActiveUser } = useGetMyInfoQuery();
  const { data: bookmarks } = useBookmark({ isActiveUser });
  const { toastShow } = useToastStore();
  const { getIndustryByCode } = useGetIndustryByCode();
  const [isOpen, setIsOpen] = useState(false);
  const { mutate: addBookmark, isLoading: addBookmarkLoading } = usePostBookmarks();
  const { mutate: deleteBookmark, isLoading: deleteBookmarkLoading } = useDeleteBookmarks();
  const movieOneIdPage = useMoveOneIdPage();
  const theme = useTheme();
  const isBookmarked = !!bookmarks
    ?.map((bookmark) => bookmark.PK_NM_HASH)
    .includes(companyInfo?.PK_NM_HASH ?? '');

  const handleBookMark = () => {
    if (companyInfo) {
      if (isActiveUser) {
        if (!addBookmarkLoading && !deleteBookmarkLoading) {
          if (isBookmarked) {
            deleteBookmark(companyInfo.PK_NM_HASH);
          } else {
            addBookmark(companyInfo.PK_NM_HASH);
          }

          setGtmDataLayer('company__bookmark__click', {
            companyName: companyInfo?.name,
            companyType: companyInfo?.category ?? '',
            companyIndustry: companyInfo?.industryCode
              ? getIndustryByCode(companyInfo?.industryCode)?.name
              : '',
            isChecked: !isBookmarked,
          });
        }
      } else {
        setGtmDataLayer('signupLogin__popup__view', {
          companyName: companyInfo?.name,
          companyHash: companyInfo.PK_NM_HASH,
          clickType: 'bookmark',
        });
        setIsOpen(true);
      }
    }
  };

  const handleToast = () => {
    copyToClipboard(window.location.href + '?utm_medium=share');
    toastShow('링크가 복사되었습니다.', {
      icon: <LinkIcon />,
    });
  };

  return (
    <>
      <S.Wrapper>
        <S.Button isActive={isBookmarked} onClick={handleBookMark} className='bookmark'>
          <ScrapIcon fill={theme.colors.gray300} strokeWidth='2' stroke={theme.colors.gray900} />
          <S.ButtonText typography='bold150_B3'>스크랩</S.ButtonText>
        </S.Button>
        <S.Button
          isActive={false}
          onClick={handleToast}
          data-attribute-id='company__share__click'
          data-company-name={companyInfo?.name}
          data-company-industry={(companyInfo && getIndustryByCode(companyInfo.industryCode)) || ''}
          data-company-type={(companyInfo && companyInfo.category) || ''}>
          <ShareIcon />
          <S.ButtonText typography='bold150_B3'>공유하기</S.ButtonText>
        </S.Button>
      </S.Wrapper>
      <Modal
        isOpen={isOpen}
        title='로그인 하시겠습니까?'
        subTitle='스크랩을 이용하려면 로그인이 필요합니다. 스크랩 후 여러 기업을 한 번에 비교해보세요.'
        confirmButtonVariant='dark'
        cancelButtonText='취소'
        confirmButtonText='로그인/회원가입'
        handleConfirm={() => {
          setGtmDataLayer('signupLogin__popup__click', {
            companyName: companyInfo?.name,
            companyHash: companyInfo?.PK_NM_HASH,
            clickType: 'bookmark',
          });
          movieOneIdPage();
          setIsOpen(false);
        }}
        handleCancel={() => {
          setGtmDataLayer('signupLogin__popup__cancel', {
            companyName: companyInfo?.name,
            companyHash: companyInfo?.PK_NM_HASH,
            clickType: 'bookmark',
          });
          setIsOpen(false);
        }}
        handleClose={() => setIsOpen(false)}
      />
    </>
  );
}

export default ActionButton;
